import {
  lazy,
  Fragment,
  type FC,
  useMemo
} from 'react';
import {
  Box,
  Stack,
  Typography
} from '@mui/joy';
import {
  alpha
} from '@mui/system';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  useCountdown
} from '@shared/lib';
import {
  SimpleCircularLoader
} from '@shared/ui';
import {
  TournamentStatusEnum,
  TOURNAMENTS_TIMEZONE_OFFSET_HOURS,
  Tournament,
} from '@entities/tournaments';
import {
  leaderboardCupImg
} from '../assets';

dayjs.extend(utc);

const AccessAlarmIcon = lazy(() => import('@mui/icons-material/AccessAlarm'));

export type LeaderboardNotAvailableProps = {
  tournament: Maybe<Tournament>;
  loading?: boolean;
};

export const LeaderboardNotAvailable: FC<LeaderboardNotAvailableProps> = ({ loading, tournament }) => {
  const { startDate } = tournament || {};
  const tournamentStartTime = useMemo(() => {
    return dayjs.utc(startDate).add(Number(TOURNAMENTS_TIMEZONE_OFFSET_HOURS), 'hours');
  }, [startDate]);

  const timeArray = useCountdown(tournamentStartTime);
  const formattedTimeArray = timeArray?.map((value) =>
    value < 10 ? `0${value}` : `${value}`
  ) || ['00', '00', '00', '00'];

  const canShowCountdown =
    tournament?.status === TournamentStatusEnum.Pending ||
    tournament?.status === TournamentStatusEnum.Active;

  return (
    <Stack
      rowGap={3}
      direction='column'
      alignItems='center'
      justifyContent='center'
      sx={
        ({ palette }) => ({
          top: 0,
          width: '100%',
          height: '100%',
          borderRadius: 8,
          position: 'relative',
          backgroundColor: palette.common[565],
          boxShadow: `inset 0 0 13px 0 ${alpha(palette.common[315], 0.35)}`,
          ...(canShowCountdown && ({
            marginTop: 2
          }))
        })}>
      {loading ? (
        <SimpleCircularLoader />
      ) : (
        <Fragment>
          {canShowCountdown && (
            <Stack
              direction='row'
              alignItems='center'
              sx={({ palette }) => ({
                top: 0,
                left: '50%',
                minWidth: 195,
                borderWidth: 1,
                borderRadius: 16,
                paddingBlock: 0.5,
                paddingInline: 1.5,
                position: 'absolute',
                borderStyle: 'solid',
                borderColor: palette.common[315],
                transform: 'translate(-50%, -50%)',
                backgroundColor: palette.common[565],
                boxShadow: `0 2px 12px 0 ${alpha(palette.common[335], 0.5)}, inset 0 0 13px 0 ${alpha(palette.common[315], 0.35)}`
              })}>
              <AccessAlarmIcon sx={({ palette }) => ({
                fontSize: 16,
                marginRight: 0.5,
                marginBottom: 0.2,
                color: palette.common.white,
              })}
              />
              <Typography sx={({ palette }) => ({
                fontSize: 16,
                fontWeight: 500,
                color: palette.common.white
              })}>
                Starts in&nbsp;
              </Typography>
              <Typography sx={({ palette }) => ({
                fontSize: 16,
                fontWeight: 500,
                color: palette.common.white
              })}>
                {formattedTimeArray[0]}:
              </Typography>
              <Typography sx={({ palette }) => ({
                fontSize: 16,
                fontWeight: 500,
                color: palette.common.white
              })}>
                {formattedTimeArray[1]}:
              </Typography>
              <Typography sx={({ palette }) => ({
                fontSize: 16,
                fontWeight: 500,
                color: palette.common.white
              })}>
                {formattedTimeArray[2]}:
              </Typography>
              <Typography sx={({ palette }) => ({
                fontSize: 16,
                fontWeight: 500,
                color: palette.common.white
              })}>
                {formattedTimeArray[3]}
              </Typography>
            </Stack>
          )}
          <Box textAlign='center'>
            <Typography
              sx={({ palette }) => ({
                fontSize: 20,
                fontWeight: 600,
                color: palette.common.white
              })}>
              {timeArray ? 'Leaderboard will unlock soon' : 'Race has started!'}
            </Typography>
            <Typography
              sx={({ palette }) => ({
                fontSize: 14,
                fontWeight: 400,
                width: '100%',
                maxWidth: 300,
                color: palette.common.white
              })}>
              {
                timeArray
                  ? 'It will be available once the race starts.'
                  : 'Leaderboard will appear as soon as someone joins the race.'
              }
            </Typography>
          </Box>
          <Stack position='relative'>
            <Box
              width={160}
              component='img'
              src={leaderboardCupImg.default}
            />
            <Box sx={({ palette }) => ({
              opacity: 0.5,
              top: '50%',
              left: '50%',
              width: '100%',
              height: '100%',
              borderRadius: '100%',
              position: 'absolute',
              filter: 'blur(64px)',
              transform: 'translate(-50%, -50%)',
              backgroundColor: palette.common[1065],
            })} />
          </Stack>
        </Fragment>
      )}
    </Stack >
  );
};